import { type FontFamily, type OrgFont, setCustomFonts } from "@cva/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { orpc } from "./api";

/**
 * Fonts of the organization (uploads, imported Google Fonts). They are served
 * from our own storage, registered as FontFace objects and made known to the
 * shared font helpers (weights, italics), so pickers and the renderer use them
 * like the built-in fonts.
 */

const loaded = new Map<string, globalThis.FontFace>();

function registerFaces(fonts: OrgFont[]) {
  for (const font of fonts) {
    for (const face of font.faces) {
      if (loaded.has(face.url)) continue;
      const fontFace = new FontFace(font.family, `url("${face.url}")`, {
        weight: String(face.weight),
        style: face.style,
        display: "swap",
        ...(face.unicodeRange ? { unicodeRange: face.unicodeRange } : {}),
      });
      loaded.set(face.url, fontFace);
      document.fonts.add(fontFace);
    }
  }
}

export function toFontFamily(font: OrgFont): FontFamily {
  const weights = [...new Set(font.faces.map((face) => face.weight))].sort((a, b) => a - b);
  const italics = [
    ...new Set(font.faces.filter((face) => face.style === "italic").map((face) => face.weight)),
  ].sort((a, b) => a - b);
  return { family: font.family, category: "custom", weights, italics };
}

/** Loads the organization's fonts; returns them once registered. */
export function useOrgFonts(orgId: string) {
  const query = useQuery({
    ...orpc.fonts.list.queryOptions({ input: { orgId } }),
    staleTime: 60_000,
  });
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (!query.data) return;
    registerFaces(query.data);
    setCustomFonts(query.data.map(toFontFamily));
    setVersion((value) => value + 1);
  }, [query.data]);
  const families = useMemo(
    // `version` makes consumers re-render after the registry changed.
    () => (version >= 0 ? (query.data ?? []).map(toFontFamily) : []),
    [query.data, version],
  );
  return {
    fonts: query.data ?? [],
    families,
    ready: query.isSuccess,
    /** The fonts are known to the browser (FontFace objects added). */
    registered: query.isSuccess && (version > 0 || query.data.length === 0),
  };
}
